/**
 * Tracking Pixels Service v2.0
 *
 * Initializes and manages tracking pixels for multi-tenant checkout.
 * Each store can have their own analytics pixels for attribution.
 *
 * NEW in v2.0:
 * - Advanced Matching for Facebook Pixel (hashed customer data)
 * - Event deduplication with eventID
 * - Facebook Cookies capture (_fbc, _fbp)
 * - Google Enhanced Conversions support
 * - Tracking params helpers
 *
 * Supported platforms:
 * - Google Tag Manager
 * - Facebook Pixel + Conversions API
 * - Google Ads + Enhanced Conversions
 * - TikTok Pixel
 * - Snapchat Pixel
 * - Twitter/X Pixel
 * - Pinterest Tag
 */
import logger from '../utils/logger';

// =====================================================
// MODULE STATE
// =====================================================

/**
 * Track if Facebook Pixel has been initialized on this page load.
 * Prevents duplicate fbq('init') calls which trigger Meta's warning:
 * "[Meta Pixel] - Duplicate Pixel ID"
 *
 * This resets on page navigation (full reload).
 */
let fbPixelInitialized = false;

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Generate unique event ID for deduplication with Conversions API
 * @param {string} prefix - Event prefix (e.g., 'purchase', 'checkout')
 * @param {string} identifier - Unique identifier (e.g., order_id, cart_id)
 * @returns {string} Unique event ID
 */
export const generateEventId = (prefix, identifier) => {
  return `${prefix}_${identifier}_${Date.now()}`;
};

/**
 * Get Facebook cookies for Conversions API
 * @returns {Object} Object with fbc and fbp values
 */
export const getFacebookCookies = () => {
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  return {
    fbc: getCookie('_fbc'),   // Facebook Click ID cookie
    fbp: getCookie('_fbp')    // Facebook Browser ID cookie
  };
};

/**
 * Get tracking parameters from URL
 * @returns {Object} Object with tracking params (fbclid, gclid, UTMs, etc.)
 */
export const getTrackingParamsFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  const tracking = {};

  // Click IDs
  ['fbclid', 'gclid', 'ttclid', 'sclid', 'twclid', 'li_fat_id', 'msclkid'].forEach(key => {
    if (params.has(key)) tracking[key] = params.get(key);
  });

  // UTM parameters
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
    if (params.has(key)) tracking[key] = params.get(key);
  });

  return tracking;
};

/**
 * Reconstruct fbc cookie value from fbclid if cookie doesn't exist
 * @param {string} fbclid - Facebook Click ID from URL
 * @returns {string|null} Reconstructed fbc value
 */
export const reconstructFbc = (fbclid) => {
  if (!fbclid) return null;
  // Format: fb.{version}.{creation_timestamp}.{fbclid}
  return `fb.1.${Date.now()}.${fbclid}`;
};

// =====================================================
// INITIALIZATION FUNCTIONS
// =====================================================

/**
 * Initialize Google Tag Manager
 * @param {string} containerId - GTM container ID (GTM-XXXXXX)
 */
export const initializeGTM = (containerId) => {
  if (!containerId) return;

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // Inject GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);

  logger.debug(`GTM initialized: ${containerId}`);
};

/**
 * Initialize Facebook Pixel (Basic - without Advanced Matching)
 * @param {string} pixelId - Facebook Pixel ID
 */
export const initializeFacebookPixel = (pixelId) => {
  if (!pixelId) return;

  // Prevent duplicate init on same page (causes Meta warning)
  if (fbPixelInitialized) {
    logger.debug(`ℹ️ Facebook Pixel already initialized, skipping: ${pixelId}`);
    return;
  }

  // Facebook Pixel Base Code
  /* eslint-disable no-unused-expressions */
  (function(f,b,e,v,n,t,s) {
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s);
  })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable no-unused-expressions */

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

  fbPixelInitialized = true;
  logger.debug(`✓ Facebook Pixel initialized: ${pixelId}`);
};

/**
 * Initialize Facebook Pixel with Advanced Matching
 * Sends hashed customer data for better attribution
 * @param {string} pixelId - Facebook Pixel ID
 * @param {Object} userData - Customer data (email, phone, name, address)
 */
export const initializeFacebookPixelWithAM = (pixelId, userData = {}) => {
  if (!pixelId) return;

  // Prevent duplicate init on same page (causes Meta warning)
  if (fbPixelInitialized) {
    logger.debug(`ℹ️ Facebook Pixel already initialized, skipping: ${pixelId}`);
    return;
  }

  // Facebook Pixel Base Code
  /* eslint-disable no-unused-expressions */
  (function(f,b,e,v,n,t,s) {
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s);
  })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable no-unused-expressions */

  // Build Advanced Matching data (Facebook will hash it automatically)
  const amData = {};
  if (userData.email) amData.em = userData.email.toLowerCase().trim();
  if (userData.phone) amData.ph = userData.phone.replace(/\D/g, ''); // Digits only
  if (userData.firstName) amData.fn = userData.firstName.toLowerCase().trim();
  if (userData.lastName) amData.ln = userData.lastName.toLowerCase().trim();
  if (userData.city) amData.ct = userData.city.toLowerCase().replace(/\s/g, '');
  if (userData.state) amData.st = userData.state.toLowerCase().trim();
  if (userData.zip) amData.zp = userData.zip.trim();
  if (userData.country) amData.country = userData.country.toLowerCase().trim();

  // Initialize with Advanced Matching data
  if (Object.keys(amData).length > 0) {
    window.fbq('init', pixelId, amData);
    logger.debug(`✓ Facebook Pixel initialized with Advanced Matching: ${pixelId}`, Object.keys(amData));
  } else {
    window.fbq('init', pixelId);
    logger.debug(`✓ Facebook Pixel initialized (no AM data): ${pixelId}`);
  }

  window.fbq('track', 'PageView');
  fbPixelInitialized = true;
};

/**
 * Update Facebook Advanced Matching data (call when customer enters email/phone)
 * @param {Object} userData - Updated customer data
 */
export const updateFacebookAdvancedMatching = (userData = {}) => {
  if (!window.fbq) return;

  const amData = {};
  if (userData.email) amData.em = userData.email.toLowerCase().trim();
  if (userData.phone) amData.ph = userData.phone.replace(/\D/g, '');
  if (userData.firstName) amData.fn = userData.firstName.toLowerCase().trim();
  if (userData.lastName) amData.ln = userData.lastName.toLowerCase().trim();
  if (userData.city) amData.ct = userData.city.toLowerCase().replace(/\s/g, '');
  if (userData.state) amData.st = userData.state.toLowerCase().trim();
  if (userData.zip) amData.zp = userData.zip.trim();
  if (userData.country) amData.country = userData.country.toLowerCase().trim();

  if (Object.keys(amData).length > 0) {
    // Re-init pixel with new AM data (Facebook handles deduplication)
    window.fbq('init', window._fbq_pixel_id || '', amData);
    logger.debug('✓ Facebook AM data updated:', Object.keys(amData));
  }
};

/**
 * Initialize Google Ads
 * @param {string} adsId - Google Ads ID (AW-XXXXXXXXX)
 */
export const initializeGoogleAds = (adsId) => {
  if (!adsId) return;

  // Global site tag (gtag.js)
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', adsId);

  // Inject gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${adsId}`;
  document.head.appendChild(script);

  logger.debug(`✓ Google Ads initialized: ${adsId}`);
};

/**
 * Initialize Google Analytics 4 with Cross-Domain support
 * Uses _gl parameter for session continuity across domains
 * @param {string} measurementId - GA4 Measurement ID (G-XXXXXXXXXX)
 * @param {Object} options - Configuration options
 * @param {string} options.glParam - _gl parameter from URL for cross-domain linking
 * @param {string[]} options.linkedDomains - Array of domains to enable cross-domain linking
 */
export const initializeGA4 = (measurementId, options = {}) => {
  if (!measurementId) return;

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  // Define gtag if not already defined
  if (!window.gtag) {
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
  }

  // Build GA4 config with cross-domain support
  const ga4Config = {
    send_page_view: true,
    // Enable debug mode in development
    debug_mode: process.env.NODE_ENV === 'development'
  };

  // If _gl parameter is present, GA4 will automatically use it for session linking
  // We don't need to do anything special - just having gtag loaded handles it

  // Configure cross-domain linking if domains are specified
  if (options.linkedDomains && options.linkedDomains.length > 0) {
    ga4Config.linker = {
      domains: options.linkedDomains,
      accept_incoming: true  // Accept _gl parameter from other domains
    };
  } else {
    // Default: accept incoming _gl parameter (for receiving domain)
    ga4Config.linker = {
      accept_incoming: true
    };
  }

  // Configure GA4
  window.gtag('config', measurementId, ga4Config);

  // Log _gl param if present (for debugging cross-domain)
  const glParam = new URLSearchParams(window.location.search).get('_gl');
  if (glParam) {
    logger.debug(`✓ GA4 initialized with cross-domain _gl: ${measurementId}`, glParam.substring(0, 20) + '...');
  } else {
    logger.debug(`✓ GA4 initialized: ${measurementId}`);
  }

  // Inject gtag script if not already loaded
  if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }
};

/**
 * Initialize TikTok Pixel
 * @param {string} pixelId - TikTok Pixel ID
 */
export const initializeTikTokPixel = (pixelId) => {
  if (!pixelId) return;

  // TikTok Pixel Code
  /* eslint-disable no-unused-expressions */
  (function (w, d, t) {
    w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
    ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
    ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)));};};
    for(var i=0;i<ttq.methods.length;i++){ttq.setAndDefer(ttq,ttq.methods[i]);}
    ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++){ttq.setAndDefer(e,ttq.methods[n]);}return e;};
    ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date();ttq._o=ttq._o||{};ttq._o[e]=n||{};
    var o=document.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;
    var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a);};
    ttq.load(pixelId);
    ttq.page();
  })(window, document, 'ttq');
  /* eslint-enable no-unused-expressions */

  logger.debug(`✓ TikTok Pixel initialized: ${pixelId}`);
};

/**
 * Initialize Snapchat Pixel
 * @param {string} pixelId - Snapchat Pixel ID
 */
export const initializeSnapchatPixel = (pixelId) => {
  if (!pixelId) return;

  // Snapchat Pixel Code
  (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
  {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
  a.queue=[];var s='script';var r=t.createElement(s);r.async=!0;
  r.src=n;var u=t.getElementsByTagName(s)[0];
  u.parentNode.insertBefore(r,u);})(window,document,
  'https://sc-static.net/scevent.min.js');

  window.snaptr('init', pixelId, {});
  window.snaptr('track', 'PAGE_VIEW');

  logger.debug(`✓ Snapchat Pixel initialized: ${pixelId}`);
};

/**
 * Initialize Twitter/X Pixel
 * @param {string} pixelId - Twitter Pixel ID
 */
export const initializeTwitterPixel = (pixelId) => {
  if (!pixelId) return;

  // Twitter Universal Website Tag Code
  /* eslint-disable no-unused-expressions */
  (function(e,t,n,s,u,a){
    if(e.twq)return;
    s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);};
    s.version='1.1';s.queue=[];u=t.createElement(n);u.async=!0;u.src='https://static.ads-twitter.com/uwt.js';
    a=t.getElementsByTagName(n)[0];a.parentNode.insertBefore(u,a);
  })(window,document,'script');
  /* eslint-enable no-unused-expressions */

  window.twq('config', pixelId);

  logger.debug(`✓ Twitter Pixel initialized: ${pixelId}`);
};

/**
 * Initialize Pinterest Tag
 * @param {string} tagId - Pinterest Tag ID
 */
export const initializePinterestTag = (tagId) => {
  if (!tagId) return;

  // Pinterest Tag Code
  /* eslint-disable no-unused-expressions */
  (function(e){
    if(!window.pintrk){
      window.pintrk = function () {
        window.pintrk.queue.push(Array.prototype.slice.call(arguments));
      };
      var n=window.pintrk;n.queue=[];n.version="3.0";
      var t=document.createElement("script");t.async=!0;t.src=e;
      var r=document.getElementsByTagName("script")[0];
      r.parentNode.insertBefore(t,r);
    }
  })("https://s.pinimg.com/ct/core.js");
  /* eslint-enable no-unused-expressions */

  window.pintrk('load', tagId);
  window.pintrk('page');

  logger.debug(`✓ Pinterest Tag initialized: ${tagId}`);
};

// =====================================================
// MAIN INITIALIZATION
// =====================================================

/**
 * Initialize all tracking pixels based on store configuration
 * @param {Object} tracking - Tracking configuration from store
 * @param {string} customHeadScript - Custom script to inject in <head>
 * @param {string} customBodyScript - Custom script to inject at end of <body>
 */
export const initializeTracking = (tracking, customHeadScript, customBodyScript) => {
  logger.debug('🎯 Initializing tracking pixels...');

  // Initialize each platform
  if (tracking.gtm_container_id) {
    initializeGTM(tracking.gtm_container_id);
  }

  if (tracking.facebook_pixel_id) {
    initializeFacebookPixel(tracking.facebook_pixel_id);
  }

  // Initialize GA4 with cross-domain support (before Google Ads for proper gtag config)
  if (tracking.google_analytics_id) {
    initializeGA4(tracking.google_analytics_id);
  }

  if (tracking.google_ads_id) {
    initializeGoogleAds(tracking.google_ads_id);
  }

  if (tracking.tiktok_pixel_id) {
    initializeTikTokPixel(tracking.tiktok_pixel_id);
  }

  if (tracking.snapchat_pixel_id) {
    initializeSnapchatPixel(tracking.snapchat_pixel_id);
  }

  if (tracking.twitter_pixel_id) {
    initializeTwitterPixel(tracking.twitter_pixel_id);
  }

  if (tracking.pinterest_tag_id) {
    initializePinterestTag(tracking.pinterest_tag_id);
  }

  // Inject custom scripts
  if (customHeadScript) {
    const headDiv = document.createElement('div');
    headDiv.innerHTML = customHeadScript;
    document.head.appendChild(headDiv);
    logger.debug('✓ Custom head script injected');
  }

  if (customBodyScript) {
    const bodyDiv = document.createElement('div');
    bodyDiv.innerHTML = customBodyScript;
    document.body.appendChild(bodyDiv);
    logger.debug('✓ Custom body script injected');
  }

  logger.debug('✅ Tracking initialization complete');
};

// =====================================================
// EVENT TRACKING
// =====================================================

/**
 * Track checkout started event
 * @param {Object} tracking - Tracking configuration
 * @param {Object} cartData - Cart data (items, subtotal, etc.)
 * @param {string} eventId - Optional event ID for deduplication
 * @returns {string} The event ID used (for passing to backend)
 */
export const trackCheckoutStarted = (tracking, cartData, eventId = null) => {
  logger.debug('📊 Tracking: Checkout Started');

  // Generate event ID if not provided
  const evtId = eventId || generateEventId('initiate_checkout', cartData.cart_id || Date.now());

  // GTM
  if (tracking.gtm_container_id && window.dataLayer) {
    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'USD',
        value: parseFloat(cartData.subtotal),
        items: cartData.items.map((item, index) => ({
          item_id: item.shopify_id || item.product_id,
          item_name: item.product_name || item.name,
          price: parseFloat(item.price),
          quantity: item.quantity,
          index: index
        }))
      }
    });
  }

  // Facebook Pixel with eventID for CAPI deduplication
  if (tracking.facebook_pixel_id && window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_type: 'product',
      content_ids: cartData.items.map(item => String(item.shopify_id || item.product_id)),
      contents: cartData.items.map(item => ({
        id: String(item.shopify_id || item.product_id),
        quantity: item.quantity,
        item_price: parseFloat(item.price)
      })),
      value: parseFloat(cartData.subtotal),
      currency: 'USD',
      num_items: cartData.items.reduce((sum, item) => sum + item.quantity, 0)
    }, {
      eventID: evtId  // For CAPI deduplication
    });
  }

  // TikTok Pixel
  if (tracking.tiktok_pixel_id && window.ttq) {
    window.ttq.track('InitiateCheckout', {
      contents: cartData.items.map(item => ({
        content_id: String(item.shopify_id || item.product_id),
        content_name: item.product_name || item.name,
        quantity: item.quantity,
        price: parseFloat(item.price)
      })),
      value: parseFloat(cartData.subtotal),
      currency: 'USD'
    });
  }

  // Snapchat Pixel
  if (tracking.snapchat_pixel_id && window.snaptr) {
    window.snaptr('track', 'START_CHECKOUT', {
      item_ids: cartData.items.map(item => String(item.shopify_id || item.product_id)),
      price: parseFloat(cartData.subtotal),
      currency: 'USD'
    });
  }

  // Twitter Pixel
  if (tracking.twitter_pixel_id && window.twq) {
    window.twq('event', 'tw-xxxxx-xxxxx', {
      value: parseFloat(cartData.subtotal),
      currency: 'USD'
    });
  }

  // Pinterest Tag
  if (tracking.pinterest_tag_id && window.pintrk) {
    window.pintrk('track', 'checkout', {
      value: parseFloat(cartData.subtotal),
      currency: 'USD',
      line_items: cartData.items.map(item => ({
        product_id: String(item.shopify_id || item.product_id),
        product_name: item.product_name || item.name,
        product_price: parseFloat(item.price),
        product_quantity: item.quantity
      }))
    });
  }

  return evtId;
};

/**
 * Track AddPaymentInfo event (when customer enters payment details)
 * @param {Object} tracking - Tracking configuration
 * @param {Object} cartData - Cart data
 * @param {string} eventId - Optional event ID for deduplication
 * @returns {string} The event ID used
 */
export const trackAddPaymentInfo = (tracking, cartData, eventId = null) => {
  logger.debug('📊 Tracking: Add Payment Info');

  const evtId = eventId || generateEventId('add_payment_info', cartData.cart_id || Date.now());

  // GTM
  if (tracking.gtm_container_id && window.dataLayer) {
    window.dataLayer.push({
      event: 'add_payment_info',
      ecommerce: {
        currency: 'USD',
        value: parseFloat(cartData.subtotal),
        payment_type: 'credit_card'
      }
    });
  }

  // Facebook Pixel
  if (tracking.facebook_pixel_id && window.fbq) {
    window.fbq('track', 'AddPaymentInfo', {
      content_type: 'product',
      content_ids: cartData.items.map(item => String(item.shopify_id || item.product_id)),
      value: parseFloat(cartData.subtotal),
      currency: 'USD'
    }, {
      eventID: evtId
    });
  }

  // TikTok Pixel
  if (tracking.tiktok_pixel_id && window.ttq) {
    window.ttq.track('AddPaymentInfo', {
      value: parseFloat(cartData.subtotal),
      currency: 'USD'
    });
  }

  return evtId;
};

/**
 * Track purchase completed event
 * @param {Object} tracking - Tracking configuration
 * @param {Object} orderData - Order data (order_id, total, items, etc.)
 * @param {string} eventId - Event ID for deduplication with CAPI (CRITICAL)
 * @param {Object} customerData - Customer data for Enhanced Conversions (optional)
 * @returns {string} The event ID used
 */
export const trackPurchase = (tracking, orderData, eventId = null, customerData = null) => {
  logger.debug('📊 Tracking: Purchase Completed');

  const transactionId = orderData.order_id || orderData.transaction_id;
  const totalAmount = parseFloat(orderData.total_amount);
  const shippingCost = parseFloat(orderData.shipping_cost || 0);
  const taxAmount = parseFloat(orderData.tax_amount || 0);

  // Generate or use provided event ID (CRITICAL for CAPI deduplication)
  const evtId = eventId || generateEventId('purchase', transactionId);

  // GTM
  if (tracking.gtm_container_id && window.dataLayer) {
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: transactionId,
        value: totalAmount,
        tax: taxAmount,
        shipping: shippingCost,
        currency: 'USD',
        items: orderData.items.map((item, index) => ({
          item_id: String(item.shopify_id || item.product_id),
          item_name: item.product_name || item.name,
          price: parseFloat(item.price),
          quantity: item.quantity,
          index: index
        }))
      }
    });
  }

  // GA4 Purchase Event (via gtag from custom_head_script)
  // This fires the standard GA4 purchase event for Analytics tracking
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: totalAmount,
      tax: taxAmount,
      shipping: shippingCost,
      currency: 'USD',
      items: orderData.items.map((item, index) => ({
        item_id: String(item.shopify_id || item.product_id),
        item_name: item.product_name || item.name,
        price: parseFloat(item.price),
        quantity: item.quantity,
        index: index
      }))
    });
    logger.debug('✓ GA4 purchase event fired via gtag');
  }

  // Google Ads Conversion with Enhanced Conversions
  if (tracking.google_ads_id && tracking.google_ads_conversion_label && window.gtag) {
    const conversionData = {
      'send_to': `${tracking.google_ads_id}/${tracking.google_ads_conversion_label}`,
      'value': totalAmount,
      'currency': 'USD',
      'transaction_id': transactionId
    };

    // Add Enhanced Conversions data if available
    if (tracking.google_enhanced_conversions_enabled && customerData) {
      conversionData.user_data = {
        email: customerData.email,
        phone_number: customerData.phone,
        address: {
          first_name: customerData.firstName,
          last_name: customerData.lastName,
          city: customerData.city,
          region: customerData.state,
          postal_code: customerData.zip,
          country: customerData.country || 'US'
        }
      };
    }

    window.gtag('event', 'conversion', conversionData);
  }

  // Facebook Pixel with eventID for CAPI deduplication
  if (tracking.facebook_pixel_id && window.fbq) {
    window.fbq('track', 'Purchase', {
      content_type: 'product',
      content_ids: orderData.items.map(item => String(item.shopify_id || item.product_id)),
      contents: orderData.items.map(item => ({
        id: String(item.shopify_id || item.product_id),
        quantity: item.quantity,
        item_price: parseFloat(item.price)
      })),
      value: totalAmount,
      currency: 'USD',
      num_items: orderData.items.reduce((sum, item) => sum + item.quantity, 0)
    }, {
      eventID: evtId  // CRITICAL: Same ID used in server-side CAPI
    });
  }

  // TikTok Pixel
  if (tracking.tiktok_pixel_id && window.ttq) {
    window.ttq.track('PlaceAnOrder', {
      contents: orderData.items.map(item => ({
        content_id: String(item.shopify_id || item.product_id),
        content_name: item.product_name || item.name,
        quantity: item.quantity,
        price: parseFloat(item.price)
      })),
      value: totalAmount,
      currency: 'USD'
    });
  }

  // Snapchat Pixel
  if (tracking.snapchat_pixel_id && window.snaptr) {
    window.snaptr('track', 'PURCHASE', {
      transaction_id: transactionId,
      item_ids: orderData.items.map(item => String(item.shopify_id || item.product_id)),
      price: totalAmount,
      currency: 'USD'
    });
  }

  // Twitter Pixel
  if (tracking.twitter_pixel_id && window.twq) {
    window.twq('event', 'tw-xxxxx-xxxxx', {
      value: totalAmount,
      currency: 'USD',
      conversion_id: transactionId
    });
  }

  // Pinterest Tag
  if (tracking.pinterest_tag_id && window.pintrk) {
    window.pintrk('track', 'checkout', {
      value: totalAmount,
      order_quantity: orderData.items.reduce((sum, item) => sum + item.quantity, 0),
      currency: 'USD',
      order_id: transactionId
    });
  }

  return evtId;
};

// Default export for convenience
const trackingService = {
  // Helpers
  generateEventId,
  getFacebookCookies,
  getTrackingParamsFromURL,
  reconstructFbc,
  // Initialization
  initializeTracking,
  initializeFacebookPixel,
  initializeFacebookPixelWithAM,
  updateFacebookAdvancedMatching,
  initializeGTM,
  initializeGA4,
  initializeGoogleAds,
  initializeTikTokPixel,
  initializeSnapchatPixel,
  initializeTwitterPixel,
  initializePinterestTag,
  // Event Tracking
  trackCheckoutStarted,
  trackAddPaymentInfo,
  trackPurchase,
};

export default trackingService;
